import "./RequestProposal.css";
import { useState, useEffect } from "react";
import listCountries from "../../utils/listCountries";

const RequestProposal = ({ selectedCourse }) => {
  const courseTitle =
    selectedCourse?.post_title || selectedCourse?.title || "";

  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    phone: "",
    email: "",
    address: "",
    country: "",
    subject: courseTitle
      ? `Training Proposal Request - ${courseTitle}`
      : "Training Proposal Request",
    message: courseTitle
      ? `I would like to request a training proposal for ${courseTitle}.`
      : "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ show: false, type: "", message: "" });

  useEffect(() => {
    if (courseTitle) {
      setFormData((prev) => ({
        ...prev,
        subject: `Training Proposal Request - ${courseTitle}`,
        message: `I would like to request a training proposal for ${courseTitle}.`,
      }));
    }
  }, [courseTitle]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlertInfo({ show: false, type: "", message: "" });

    try {
      setIsSubmitting(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/proposal`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...formData, courseTitle }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send request");
      }

      setAlertInfo({
        show: true,
        type: "success",
        message: "Thank you! Your training proposal request has been sent.",
      });

      setFormData({
        fullName: "",
        companyName: "",
        phone: "",
        email: "",
        address: "",
        country: "",
        subject: "Training Proposal Request",
        message: "",
      });
    } catch (error) {
      console.error(error);
      setAlertInfo({
        show: true,
        type: "error",
        message: "Failed to send request. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="contact-page">
      <div className="container">
        <div className="page-header">
          <h1>Request Training Proposal</h1>
          <p>
            Send us your training requirements and our team will prepare a
            tailored proposal for your organization.
          </p>
        </div>

        <div className="proposal-form-container">
          <div className="contact-form-card">
            {alertInfo.show && (
              <div
                className={`form-alert ${
                  alertInfo.type === "success"
                    ? "form-alert-success"
                    : "form-alert-error"
                }`}
              >
                {alertInfo.message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid-2col">
                <div className="form-group">
                  <label htmlFor="fullName">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Full Name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="companyName">
                    Company Name <span className="required">*</span>
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Company Name"
                    required
                  />
                </div>
              </div>

              <div className="form-grid-2col">
                <div className="form-group">
                  <label htmlFor="phone">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Street Address, City, Country"
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">
                  Country <span className="required">*</span>
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select Country
                  </option>
                  {listCountries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Subject"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Specify details, target group size, preferred start date, etc."
                />
              </div>

              <button
                type="submit"
                className="proposal-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending Request..." : "Send Request"}
              </button>
            </form>
          </div>

          <aside className="contact-location-card">
            <div className="contact-card-header">
              <h2>Direct Contact</h2>
              <p>Get in touch directly with our business development team.</p>
            </div>

            <div className="contact-details">
              {/* Email */}
              <a
                className="contact-detail"
                href="mailto:inquiry@trainosys.sg"
              >
                <span className="contact-detail-icon" aria-hidden="true">
                  ✉
                </span>

                <span>
                  <small>Email</small>
                  <strong>inquiry@trainosys.sg</strong>
                </span>
              </a>

              {/* WhatsApp */}
              <a
                className="contact-detail"
                href="https://wa.me/6580910792"
                target="_blank"
                rel="noreferrer"
              >
                <span className="contact-detail-icon" aria-hidden="true">
                  ☎
                </span>

                <span>
                  <small>WhatsApp</small>
                  <strong>+65 8091 0792</strong>
                </span>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default RequestProposal;
